/**
 * JSON pull field mapping (live-wind `json` pull provider) — client-side mirror of the
 * contract in docs/plan/live-wind-pull-json/01-json-mapping-contract.md.
 *
 * The SERVER is authoritative: the backend and the Worker validate and apply the mapping.
 * Everything here exists so the configurator can react instantly (flattened field list,
 * auto-guess, a local preview of the mapped reading) without a round-trip per keystroke.
 * Save is still gated on a real server test.
 */

export type JsonWindUnit = "ms" | "kn" | "kmh" | "mph";
export type JsonDirFormat = "deg" | "compass";
export type JsonTimeMode = "auto" | "epoch_ms" | "epoch_s" | "iso" | "local" | "fetch";
export type JsonPick = "newest" | "first" | "last";

export const WIND_UNITS: JsonWindUnit[] = ["ms", "kn", "kmh", "mph"];
export const WIND_UNIT_LABELS: Record<JsonWindUnit, string> = {
  ms: "m/s",
  kn: "knots",
  kmh: "km/h",
  mph: "mph",
};
export const DIR_FORMATS: JsonDirFormat[] = ["deg", "compass"];
export const TIME_MODES: JsonTimeMode[] = ["auto", "epoch_ms", "epoch_s", "iso", "local", "fetch"];
export const TIME_MODE_LABELS: Record<JsonTimeMode, string> = {
  auto: "Auto (epoch s/ms or ISO with zone)",
  epoch_ms: "Epoch milliseconds",
  epoch_s: "Epoch seconds",
  iso: "ISO 8601 with zone",
  local: "Local wall time + time zone",
  fetch: "No timestamp — use fetch time",
};
export const PICK_RULES: JsonPick[] = ["newest", "first", "last"];

export interface JsonSpeedField {
  path: string;
  /** Null only in a DRAFT (auto-guess could not tell) — the user must pick before saving. */
  unit: JsonWindUnit | null;
}

export interface JsonDirField {
  path: string;
  format: JsonDirFormat;
}

export interface JsonTimeField {
  mode: JsonTimeMode;
  path?: string | null;
  datePath?: string | null;
  timePath?: string | null;
  tz?: string | null;
}

/** `poll_options` for provider `json`, version 1 (contract "Mapping object"). */
export interface JsonPullMapping {
  v: 1;
  records: string;
  pick: JsonPick;
  time: JsonTimeField;
  wind: JsonSpeedField;
  gust?: JsonSpeedField | null;
  dir?: JsonDirField | null;
  lat?: { path: string } | null;
  lon?: { path: string } | null;
}

/** The `preview` block of a json pull-test response. */
export interface JsonPullPreview {
  /** Null when `records` resolved to a single object. */
  recordCount: number | null;
  /** The PICKED record (or the root object if mapping failed before picking), ≤ 4 KB. */
  record: unknown;
  rootKind: "array" | "object";
  /** Server-side guess — present when the request carried no options. */
  suggested?: JsonPullMapping | null;
}

export const SURFR_STANDARD_PRESET: JsonPullMapping = {
  v: 1,
  records: "",
  pick: "newest",
  time: { mode: "auto", path: "observedAt" },
  wind: { path: "windMs", unit: "ms" },
  gust: { path: "gustMs", unit: "ms" },
  dir: { path: "dirDeg", format: "deg" },
};

export const MAC_PRESET: JsonPullMapping = {
  v: 1,
  records: "",
  pick: "newest",
  time: { mode: "local", datePath: "date", timePath: "time", tz: "Africa/Johannesburg" },
  wind: { path: "avg", unit: "kn" },
  gust: { path: "high", unit: "kn" },
  dir: { path: "dirDegrees", format: "deg" },
};

export const MAPPING_PRESETS: { id: string; label: string; mapping: JsonPullMapping }[] = [
  { id: "surfr", label: "Surfr standard", mapping: SURFR_STANDARD_PRESET },
  { id: "mac", label: "MAC-style (date+time local)", mapping: MAC_PRESET },
];

export const cloneMapping = (m: JsonPullMapping): JsonPullMapping => JSON.parse(JSON.stringify(m));

// ---------------------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------------------

export const MAX_PATH_LENGTH = 200;
export const MAX_SEGMENT_LENGTH = 64;
export const MAX_MAPPING_BYTES = 2048;

/** Null when the path is valid, otherwise a human reason. "" (root) is valid. */
export const pathError = (path: unknown): string | null => {
  if (typeof path !== "string") return "must be a string";
  if (path === "") return null;
  if (path.length > MAX_PATH_LENGTH) return `longer than ${MAX_PATH_LENGTH} characters`;
  const segs = path.split(".");
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    if (s.length === 0) return "has an empty segment";
    if (s.length > MAX_SEGMENT_LENGTH) return `has a segment longer than ${MAX_SEGMENT_LENGTH} characters`;
  }
  return null;
};

const ABSENT = undefined;

/** Resolve a dot path; `undefined` = absent (missing key, out-of-range index, non-container). */
export const resolvePath = (doc: unknown, path: string): unknown => {
  if (path === "") return doc;
  let cur: unknown = doc;
  const segs = path.split(".");
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i];
    if (cur === null || typeof cur !== "object") return ABSENT;
    if (/^\d+$/.test(seg)) {
      if (!Array.isArray(cur)) return ABSENT;
      const idx = Number(seg);
      if (idx >= cur.length) return ABSENT;
      cur = cur[idx];
    } else {
      if (Array.isArray(cur)) return ABSENT;
      const obj = cur as Record<string, unknown>;
      if (!Object.prototype.hasOwnProperty.call(obj, seg)) return ABSENT;
      cur = obj[seg];
    }
  }
  return cur;
};

export interface FlatField {
  path: string;
  value: unknown;
  /** "number" | "string" | "boolean" | "null" */
  kind: string;
}

/**
 * Flatten a record into addressable leaf paths (`path → value`). Keys that cannot be
 * expressed in the path syntax (containing ".", empty, > 64 chars) are skipped — the user
 * could not map them anyway.
 */
export const flattenRecord = (record: unknown, maxDepth = 6, maxFields = 400): FlatField[] => {
  const out: FlatField[] = [];
  const walk = (node: unknown, prefix: string, depth: number) => {
    if (out.length >= maxFields) return;
    if (node === null || typeof node !== "object") {
      if (prefix === "") return;
      out.push({ path: prefix, value: node, kind: node === null ? "null" : typeof node });
      return;
    }
    if (depth >= maxDepth) return;
    if (Array.isArray(node)) {
      node.forEach((child, i) => walk(child, prefix ? `${prefix}.${i}` : String(i), depth + 1));
      return;
    }
    Object.keys(node as Record<string, unknown>).forEach((k) => {
      if (!k || k.includes(".") || k.length > MAX_SEGMENT_LENGTH) return;
      const p = prefix ? `${prefix}.${k}` : k;
      if (p.length > MAX_PATH_LENGTH) return;
      walk((node as Record<string, unknown>)[k], p, depth + 1);
    });
  };
  walk(record, "", 0);
  return out;
};

// ---------------------------------------------------------------------------------------
// Value reading
// ---------------------------------------------------------------------------------------

const DECIMAL_RE = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

/** JSON number, or a string that parses FULLY as a finite decimal. Else null (absent). */
export const readNumber = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t || !DECIMAL_RE.test(t)) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const round3 = (n: number) => Math.round(n * 1000) / 1000;

export const toMs = (value: number, unit: JsonWindUnit): number => {
  switch (unit) {
    case "kn":
      return value * (1 / 1.94384);
    case "kmh":
      return value / 3.6;
    case "mph":
      return value * 0.44704;
    default:
      return value;
  }
};

const plausibleWindMs = (ms: number | null): number | null =>
  ms == null || !Number.isFinite(ms) || ms < 0 || ms > 120 ? null : ms;

const clampDir = (d: number): number | null => {
  if (!Number.isFinite(d)) return null;
  const r = ((d % 360) + 360) % 360;
  return r;
};

export const COMPASS_POINTS = [
  "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW",
];

export const compassToDeg = (s: string): number | null => {
  const i = COMPASS_POINTS.indexOf(s.trim().toUpperCase());
  return i < 0 ? null : i * 22.5;
};

export const degToCompass = (deg: number | null | undefined): string => {
  if (deg == null || !Number.isFinite(deg)) return "—";
  const i = Math.round((((deg % 360) + 360) % 360) / 22.5) % 16;
  return COMPASS_POINTS[i];
};

// ---------------------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------------------

const ISO_ZONED_RE =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(Z|[+-]\d{2}:?\d{2})$/i;
const LOCAL_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

/** Epoch ms for an ISO string WITH zone; "no_zone" when it has none; null when unparseable. */
export const parseIsoZoned = (s: string): number | "no_zone" | null => {
  const t = s.trim();
  const m = ISO_ZONED_RE.exec(t);
  if (m) {
    let zone = m[3].toUpperCase();
    if (zone !== "Z" && !zone.includes(":")) zone = `${zone.slice(0, 3)}:${zone.slice(3)}`;
    const ms = Date.parse(`${m[1]}T${m[2]}${zone}`);
    return Number.isFinite(ms) ? ms : null;
  }
  if (LOCAL_DATETIME_RE.test(t) || /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}\.\d+$/.test(t)) {
    return "no_zone";
  }
  return null;
};

export const isValidTimeZone = (tz: unknown): boolean => {
  if (typeof tz !== "string" || !tz.trim()) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/** Offset (ms) of `tz` at instant `utcMs`: local wall clock minus UTC. */
const tzOffsetMs = (utcMs: number, tz: string): number => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts: Record<string, string> = {};
  dtf.formatToParts(new Date(utcMs)).forEach((p) => {
    parts[p.type] = p.value;
  });
  const hour = Number(parts.hour) % 24;
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second)
  );
  const truncated = utcMs - (((utcMs % 1000) + 1000) % 1000);
  return asUtc - truncated;
};

/**
 * Interpret a wall-clock time in `tz`. DST-correct; for an ambiguous or nonexistent wall
 * time the EARLIER offset (the one in force before the transition) is used — the same rule
 * as java.time's ZonedDateTime.of.
 */
export const zonedWallTimeToUtc = (
  y: number, mo: number, d: number, h: number, mi: number, s: number, tz: string
): number => {
  const wall = Date.UTC(y, mo - 1, d, h, mi, s);
  // Offsets before and after any transition near the wall time; the earlier one wins.
  const offA = tzOffsetMs(wall - 12 * 3600_000, tz);
  const offB = tzOffsetMs(wall + 12 * 3600_000, tz);
  const candidates = [offA, offB];
  for (let i = 0; i < candidates.length; i++) {
    const t = wall - candidates[i];
    if (tzOffsetMs(t, tz) === candidates[i]) return t;
  }
  // Nonexistent wall time (DST gap): fall back to the earlier offset.
  return wall - candidates[0];
};

export type TimeReadResult = { ms: number } | { error: "time_missing" | "time_no_zone" | "time_unparseable" };

export const readTime = (record: unknown, time: JsonTimeField, fetchedAt: number): TimeReadResult => {
  if (time.mode === "fetch") return { ms: fetchedAt };
  if (time.mode === "local") {
    const tz = time.tz ?? "";
    if (!isValidTimeZone(tz)) return { error: "time_unparseable" };
    let parts: number[] | null = null;
    if (time.path) {
      const raw = resolvePath(record, time.path);
      if (raw === undefined || raw === null || raw === "") return { error: "time_missing" };
      const m = typeof raw === "string" ? LOCAL_DATETIME_RE.exec(raw.trim()) : null;
      if (!m) return { error: "time_unparseable" };
      parts = [+m[1], +m[2], +m[3], +m[4], +m[5], m[6] ? +m[6] : 0];
    } else if (time.datePath && time.timePath) {
      const dRaw = resolvePath(record, time.datePath);
      const tRaw = resolvePath(record, time.timePath);
      if (dRaw == null || tRaw == null || dRaw === "" || tRaw === "") return { error: "time_missing" };
      const dm = typeof dRaw === "string" ? DATE_RE.exec(dRaw.trim()) : null;
      const tm = typeof tRaw === "string" ? TIME_RE.exec(tRaw.trim()) : null;
      if (!dm || !tm) return { error: "time_unparseable" };
      parts = [+dm[1], +dm[2], +dm[3], +tm[1], +tm[2], tm[3] ? +tm[3] : 0];
    } else {
      return { error: "time_missing" };
    }
    const [y, mo, d, h, mi, s] = parts;
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || s > 59) return { error: "time_unparseable" };
    return { ms: zonedWallTimeToUtc(y, mo, d, h, mi, s, tz) };
  }
  if (!time.path) return { error: "time_missing" };
  const raw = resolvePath(record, time.path);
  if (raw === undefined || raw === null || raw === "") return { error: "time_missing" };
  if (time.mode === "epoch_ms" || time.mode === "epoch_s") {
    const n = readNumber(raw);
    if (n == null) return { error: "time_unparseable" };
    return { ms: Math.round(time.mode === "epoch_s" ? n * 1000 : n) };
  }
  if (time.mode === "iso") {
    if (typeof raw !== "string") return { error: "time_unparseable" };
    const r = parseIsoZoned(raw);
    if (r === "no_zone") return { error: "time_no_zone" };
    return r == null ? { error: "time_unparseable" } : { ms: r };
  }
  // auto
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return { error: "time_unparseable" };
    return { ms: Math.round(raw < 1e11 ? raw * 1000 : raw) };
  }
  if (typeof raw === "string") {
    const r = parseIsoZoned(raw);
    if (r === "no_zone") return { error: "time_no_zone" };
    if (r == null) return { error: "time_unparseable" };
    return { ms: r };
  }
  return { error: "time_unparseable" };
};

// ---------------------------------------------------------------------------------------
// Applying a mapping (local preview)
// ---------------------------------------------------------------------------------------

export const STALE_MS = 2 * 3600_000;
export const FUTURE_MS = 10 * 60_000;

export type MappingErrorCode =
  | "not_json"
  | "records_not_found"
  | "no_records"
  | "wind_missing"
  | "time_missing"
  | "time_no_zone"
  | "time_unparseable"
  | "stale"
  | "future"
  | "bad_mapping";

export interface MappedReading {
  windMs: number | null;
  gustMs: number | null;
  dirDeg: number | null;
  observedAt: number | null;
  error: MappingErrorCode | null;
}

/** Apply the FIELD part of a mapping to one already-picked record. */
export const applyToRecord = (
  record: unknown,
  m: JsonPullMapping,
  fetchedAt: number,
  opts: { checkFreshness?: boolean } = {}
): MappedReading => {
  const fail = (error: MappingErrorCode): MappedReading => ({
    windMs: null, gustMs: null, dirDeg: null, observedAt: null, error,
  });
  if (!m.wind?.unit) return fail("bad_mapping");
  const windRaw = readNumber(resolvePath(record, m.wind.path));
  const windMs = plausibleWindMs(windRaw == null ? null : round3(toMs(windRaw, m.wind.unit)));
  if (windMs == null) return fail("wind_missing");
  let gustMs: number | null = null;
  if (m.gust && m.gust.path && m.gust.unit) {
    const g = readNumber(resolvePath(record, m.gust.path));
    gustMs = plausibleWindMs(g == null ? null : round3(toMs(g, m.gust.unit)));
    if (gustMs != null && gustMs < windMs) gustMs = windMs;
  }
  let dirDeg: number | null = null;
  if (m.dir && m.dir.path) {
    const raw = resolvePath(record, m.dir.path);
    if (m.dir.format === "compass") {
      dirDeg = typeof raw === "string" ? compassToDeg(raw) : null;
    } else {
      const n = readNumber(raw);
      dirDeg = n == null ? null : clampDir(n);
    }
  }
  const t = readTime(record, m.time, fetchedAt);
  if ("error" in t) return fail(t.error);
  if (opts.checkFreshness !== false && m.time.mode !== "fetch") {
    if (fetchedAt - t.ms > STALE_MS) return { windMs, gustMs, dirDeg, observedAt: t.ms, error: "stale" };
    if (t.ms - fetchedAt > FUTURE_MS) return { windMs, gustMs, dirDeg, observedAt: t.ms, error: "future" };
  }
  return { windMs, gustMs, dirDeg, observedAt: t.ms, error: null };
};

/** Full client-side parse of a fetched body (records + pick + fields). Mirrors the Worker. */
export const parseJsonMapped = (body: string, fetchedAt: number, m: JsonPullMapping): MappedReading => {
  const fail = (error: MappingErrorCode): MappedReading => ({
    windMs: null, gustMs: null, dirDeg: null, observedAt: null, error,
  });
  if (validateMapping(m).length) return fail("bad_mapping");
  let doc: unknown;
  try {
    doc = JSON.parse(body);
  } catch {
    return fail("not_json");
  }
  const target = resolvePath(doc, m.records);
  if (target === undefined || target === null || typeof target !== "object") return fail("records_not_found");
  if (!Array.isArray(target)) return applyToRecord(target, m, fetchedAt);
  const recs = target.filter((r) => r !== null && typeof r === "object");
  if (recs.length === 0) return fail("no_records");
  if (m.pick === "first") return applyToRecord(recs[0], m, fetchedAt);
  if (m.pick === "last") return applyToRecord(recs[recs.length - 1], m, fetchedAt);
  // newest by parsed time; ties -> first
  let best: unknown = recs[0];
  let bestMs = -Infinity;
  recs.forEach((r) => {
    const t = readTime(r, m.time, fetchedAt);
    if ("ms" in t && t.ms > bestMs) {
      bestMs = t.ms;
      best = r;
    }
  });
  return applyToRecord(best, m, fetchedAt);
};

// ---------------------------------------------------------------------------------------
// Validation (strict — mirrors backend + Worker)
// ---------------------------------------------------------------------------------------

const TOP_KEYS = ["v", "records", "pick", "time", "wind", "gust", "dir", "lat", "lon"];
const TIME_KEYS = ["mode", "path", "datePath", "timePath", "tz"];

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/** Returns a list of human-readable problems; empty = valid. Accepts untrusted input. */
export const validateMapping = (input: unknown): string[] => {
  const errs: string[] = [];
  if (!isObj(input)) return ["Mapping must be a JSON object."];
  Object.keys(input).forEach((k) => {
    if (TOP_KEYS.indexOf(k) < 0) errs.push(`Unknown key "${k}".`);
  });
  if (input.v !== 1) errs.push(`"v" must be 1.`);
  const checkPath = (label: string, p: unknown) => {
    const e = pathError(p);
    if (e) errs.push(`${label} path ${e}.`);
  };
  if (input.records === undefined) errs.push(`"records" is required ("" = the document root).`);
  else checkPath("records", input.records);
  if (PICK_RULES.indexOf(input.pick as JsonPick) < 0) errs.push(`"pick" must be one of ${PICK_RULES.join(", ")}.`);

  const speed = (label: string, f: unknown, required: boolean) => {
    if (f === undefined || f === null) {
      if (required) errs.push(`"${label}" is required.`);
      return;
    }
    if (!isObj(f)) {
      errs.push(`"${label}" must be an object.`);
      return;
    }
    Object.keys(f).forEach((k) => {
      if (k !== "path" && k !== "unit") errs.push(`Unknown key "${label}.${k}".`);
    });
    if (typeof f.path !== "string" || !f.path) errs.push(`"${label}.path" is required.`);
    else checkPath(label, f.path);
    if (WIND_UNITS.indexOf(f.unit as JsonWindUnit) < 0) {
      errs.push(`"${label}.unit" must be one of ${WIND_UNITS.join(", ")} — pick the unit the source uses.`);
    }
  };
  speed("wind", input.wind, true);
  speed("gust", input.gust, false);

  if (input.dir !== undefined && input.dir !== null) {
    const d = input.dir;
    if (!isObj(d)) errs.push(`"dir" must be an object.`);
    else {
      Object.keys(d).forEach((k) => {
        if (k !== "path" && k !== "format") errs.push(`Unknown key "dir.${k}".`);
      });
      if (typeof d.path !== "string" || !d.path) errs.push(`"dir.path" is required.`);
      else checkPath("dir", d.path);
      if (DIR_FORMATS.indexOf(d.format as JsonDirFormat) < 0) errs.push(`"dir.format" must be deg or compass.`);
    }
  }
  (["lat", "lon"] as const).forEach((k) => {
    const f = input[k];
    if (f === undefined || f === null) return;
    if (!isObj(f)) {
      errs.push(`"${k}" must be an object.`);
      return;
    }
    Object.keys(f).forEach((kk) => {
      if (kk !== "path") errs.push(`Unknown key "${k}.${kk}".`);
    });
    if (typeof f.path !== "string" || !f.path) errs.push(`"${k}.path" is required.`);
    else checkPath(k, f.path);
  });

  const t = input.time;
  if (!isObj(t)) errs.push(`"time" is required.`);
  else {
    Object.keys(t).forEach((k) => {
      if (TIME_KEYS.indexOf(k) < 0) errs.push(`Unknown key "time.${k}".`);
    });
    const mode = t.mode as JsonTimeMode;
    if (TIME_MODES.indexOf(mode) < 0) errs.push(`"time.mode" must be one of ${TIME_MODES.join(", ")}.`);
    (["path", "datePath", "timePath"] as const).forEach((k) => {
      if (t[k] !== undefined && t[k] !== null) checkPath(`time.${k}`, t[k]);
    });
    const has = (k: string) => typeof t[k] === "string" && (t[k] as string).length > 0;
    if (mode === "local") {
      if (!has("tz")) errs.push(`"time.tz" is required for local time.`);
      else if (!isValidTimeZone(t.tz)) errs.push(`"time.tz" is not a valid IANA time zone.`);
      if (!has("path") && !(has("datePath") && has("timePath"))) {
        errs.push(`Local time needs either "time.path" or both "time.datePath" and "time.timePath".`);
      }
    } else if (mode && mode !== "fetch" && TIME_MODES.indexOf(mode) >= 0 && !has("path")) {
      errs.push(`"time.path" is required for mode ${mode}.`);
    }
  }
  try {
    if (JSON.stringify(input).length > MAX_MAPPING_BYTES) errs.push(`Mapping is larger than 2 KB.`);
  } catch {
    errs.push("Mapping is not serializable.");
  }
  return errs;
};

/**
 * Wire form: drop empty optional keys so the strict server sees a minimal object.
 * Call only on a mapping the UI built (untrusted input goes through validateMapping first).
 */
export const normalizeMapping = (m: JsonPullMapping): JsonPullMapping => {
  const time: JsonTimeField = { mode: m.time.mode };
  if (m.time.mode !== "fetch") {
    if (m.time.path) time.path = m.time.path;
    if (m.time.mode === "local") {
      if (!m.time.path) {
        if (m.time.datePath) time.datePath = m.time.datePath;
        if (m.time.timePath) time.timePath = m.time.timePath;
      }
      if (m.time.tz) time.tz = m.time.tz;
    }
  }
  const out: JsonPullMapping = {
    v: 1,
    records: m.records ?? "",
    pick: m.pick ?? "newest",
    time,
    wind: { path: m.wind.path, unit: m.wind.unit },
  };
  if (m.gust && m.gust.path) out.gust = { path: m.gust.path, unit: m.gust.unit };
  if (m.dir && m.dir.path) out.dir = { path: m.dir.path, format: m.dir.format };
  if (m.lat && m.lat.path) out.lat = { path: m.lat.path };
  if (m.lon && m.lon.path) out.lon = { path: m.lon.path };
  return out;
};

/** Stable key for "was THIS exact mapping tested?". */
export const mappingKey = (m: JsonPullMapping | null | undefined): string =>
  m ? JSON.stringify(normalizeMapping(m)) : "";

/** One-line summary for review / admin tables. */
export const summarizeMapping = (m: JsonPullMapping | null | undefined): string => {
  if (!m || !m.wind) return "no mapping";
  const unit = (u: JsonWindUnit | null) => (u ? WIND_UNIT_LABELS[u] : "unit?");
  const parts = [`wind=${m.wind.path} (${unit(m.wind.unit)})`];
  if (m.gust?.path) parts.push(`gust=${m.gust.path} (${unit(m.gust.unit)})`);
  if (m.dir?.path) parts.push(`dir=${m.dir.path} (${m.dir.format})`);
  const t = m.time;
  if (t.mode === "fetch") parts.push("time=fetch time");
  else if (t.mode === "local") {
    const src = t.path ? t.path : `${t.datePath ?? "?"}+${t.timePath ?? "?"}`;
    parts.push(`time=${src} local ${t.tz ?? "tz?"}`);
  } else parts.push(`time=${t.path ?? "?"} (${t.mode})`);
  parts.push(`records=${m.records === "" ? "root" : m.records}/${m.pick}`);
  return parts.join(" · ");
};

// ---------------------------------------------------------------------------------------
// Auto-guess (mirrors the server's `suggested`)
// ---------------------------------------------------------------------------------------

const WIND_NAMES = ["windMs", "wind_ms", "windKn", "windKts", "wind_kts", "windKmh", "windSpeed", "wind_speed", "wind_avg", "windAvg", "avg", "speed", "ws"];
const GUST_NAMES = ["gustMs", "gust", "windGust", "wind_gust", "gust_kts", "wind_max", "high", "max", "wg"];
const DIR_NAMES = ["dirDeg", "dirDegrees", "windDir", "wind_dir", "wind_direction", "direction", "dir", "wd"];
const TIME_NAMES = ["observedAt", "timestamp", "ts", "time", "datetime", "date_time", "dateutc"];

/** Unit from a key suffix; null when the key does not say (the user must pick). */
export const unitFromKey = (key: string): JsonWindUnit | null => {
  if (/(Ms|_ms)$/.test(key)) return "ms";
  if (/(Kn|Kts|_kts|knots|Knots)$/.test(key)) return "kn";
  if (/(Kmh|_kmh)$/.test(key)) return "kmh";
  if (/(Mph|_mph)$/.test(key)) return "mph";
  return null;
};

/** First direct key of `record` matching a candidate (case-insensitive; candidate order wins). */
const findKey = (record: Record<string, unknown>, names: string[]): string | null => {
  const keys = Object.keys(record);
  for (let i = 0; i < names.length; i++) {
    const want = names[i].toLowerCase();
    const hit = keys.find((k) => k.toLowerCase() === want && record[k] !== null && record[k] !== undefined);
    if (hit) return hit;
  }
  return null;
};

/** Locate the records path in a document: root array, else first array-of-objects key (BFS, depth ≤ 3). */
export const guessRecordsPath = (doc: unknown): string => {
  if (Array.isArray(doc)) return "";
  if (!isObj(doc)) return "";
  const queue: { node: Record<string, unknown>; path: string; depth: number }[] = [{ node: doc, path: "", depth: 0 }];
  while (queue.length) {
    const { node, path, depth } = queue.shift()!;
    const keys = Object.keys(node);
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (!k || k.includes(".")) continue;
      const v = node[k];
      const p = path ? `${path}.${k}` : k;
      if (Array.isArray(v) && v.length > 0 && v.every((e) => isObj(e))) return p;
    }
    if (depth + 1 < 3) {
      keys.forEach((k) => {
        const v = node[k];
        if (isObj(v) && k && !k.includes(".")) queue.push({ node: v, path: path ? `${path}.${k}` : k, depth: depth + 1 });
      });
    }
  }
  return "";
};

/**
 * Guess the field mapping for ONE record. `tzDefault` fills `time.tz` when the guess is
 * local time (the UI passes the station / browser zone). Units are NEVER defaulted.
 */
export const guessFieldsFromRecord = (
  record: unknown,
  tzDefault: string | null,
  base?: Partial<Pick<JsonPullMapping, "records" | "pick">>
): JsonPullMapping => {
  const rec = isObj(record) ? record : {};
  const windKey = findKey(rec, WIND_NAMES);
  const gustKey = findKey(rec, GUST_NAMES);
  const dirKey = findKey(rec, DIR_NAMES);
  const dateKey = findKey(rec, ["date"]);
  const timeOnlyKey = findKey(rec, ["time"]);

  let time: JsonTimeField;
  const timeOnlyVal = timeOnlyKey ? rec[timeOnlyKey] : undefined;
  if (dateKey && timeOnlyKey && typeof timeOnlyVal === "string" && TIME_RE.test(timeOnlyVal.trim())) {
    time = { mode: "local", datePath: dateKey, timePath: timeOnlyKey, tz: tzDefault };
  } else {
    const tKey = findKey(rec, TIME_NAMES);
    if (!tKey) time = { mode: "fetch" };
    else {
      const v = rec[tKey];
      if (typeof v === "string" && parseIsoZoned(v) === "no_zone") {
        time = { mode: "local", path: tKey, tz: tzDefault };
      } else {
        time = { mode: "auto", path: tKey };
      }
    }
  }
  const dirVal = dirKey ? rec[dirKey] : undefined;
  return {
    v: 1,
    records: base?.records ?? "",
    pick: base?.pick ?? "newest",
    time,
    wind: { path: windKey ?? "", unit: windKey ? unitFromKey(windKey) : null },
    gust: gustKey ? { path: gustKey, unit: unitFromKey(gustKey) } : null,
    dir: dirKey ? { path: dirKey, format: readNumber(dirVal) == null ? "compass" : "deg" } : null,
  };
};

/** Full guess from a whole document (records + pick + fields). */
export const autoGuess = (doc: unknown, tzDefault: string | null): JsonPullMapping => {
  const records = guessRecordsPath(doc);
  const target = resolvePath(doc, records);
  const sample = Array.isArray(target) ? target.find((r) => isObj(r)) : target;
  return guessFieldsFromRecord(sample, tzDefault, { records, pick: "newest" });
};

// ---------------------------------------------------------------------------------------
// Time zones
// ---------------------------------------------------------------------------------------

const FALLBACK_TIME_ZONES = [
  "UTC", "Africa/Cairo", "Africa/Casablanca", "Africa/Johannesburg", "Africa/Nairobi",
  "America/Argentina/Buenos_Aires", "America/Bogota", "America/Chicago", "America/Denver",
  "America/Fortaleza", "America/Halifax", "America/Los_Angeles", "America/Mexico_City",
  "America/New_York", "America/Sao_Paulo", "America/Toronto", "Asia/Bangkok", "Asia/Dubai",
  "Asia/Hong_Kong", "Asia/Jakarta", "Asia/Kolkata", "Asia/Manila", "Asia/Singapore",
  "Asia/Tokyo", "Atlantic/Azores", "Atlantic/Canary", "Atlantic/Cape_Verde",
  "Australia/Brisbane", "Australia/Perth", "Australia/Sydney", "Europe/Amsterdam",
  "Europe/Athens", "Europe/Berlin", "Europe/Istanbul", "Europe/Lisbon", "Europe/London",
  "Europe/Madrid", "Europe/Paris", "Europe/Rome", "Indian/Mauritius", "Pacific/Auckland",
  "Pacific/Honolulu",
];

export const listTimeZones = (): string[] => {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (typeof fn === "function") {
      const list = fn("timeZone");
      if (Array.isArray(list) && list.length) return list.indexOf("UTC") >= 0 ? list : ["UTC"].concat(list);
    }
  } catch {
    // fall through
  }
  return FALLBACK_TIME_ZONES;
};

export const browserTimeZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/** Format an instant as wall time in `tz` ("YYYY-MM-DD HH:MM:SS"). */
export const formatInZone = (ms: number, tz: string | null): string => {
  try {
    const dtf = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz ?? undefined,
      hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const p: Record<string, string> = {};
    dtf.formatToParts(new Date(ms)).forEach((x) => {
      p[x.type] = x.value;
    });
    return `${p.year}-${p.month}-${p.day} ${String(Number(p.hour) % 24).padStart(2, "0")}:${p.minute}:${p.second}`;
  } catch {
    return new Date(ms).toISOString();
  }
};

/** observedAt from a pull-test sample may be epoch ms (older backend) or ISO (contract). */
export const toEpochMs = (v: number | string | null | undefined): number | null => {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const ms = Date.parse(v);
  return Number.isFinite(ms) ? ms : null;
};

/** Human-readable text for a parse/fetch error code. Fixed strings, never remote text. */
export const describeErrorCode = (code: string | null | undefined): string => {
  if (!code) return "";
  const map: Record<string, string> = {
    not_json: "The URL did not return JSON.",
    records_not_found: "The records path does not exist in the document.",
    no_records: "The records array is empty.",
    wind_missing: "The wind field is missing or not a plain number.",
    time_missing: "The time field is missing.",
    time_no_zone: "The time has no zone (Z or offset) — use mode \"local\" with a time zone.",
    time_unparseable: "The time value could not be parsed with the selected mode.",
    stale: "The newest record is more than 2 h old — wrong time zone, wrong unit, or a dead feed?",
    future: "The record is more than 10 min in the future — probably the wrong time zone.",
    bad_mapping: "The mapping is invalid.",
    timeout: "The upstream timed out.",
    redirect: "The upstream redirected — use the final URL.",
    denied_host: "That host is not allowed.",
    oversize: "The response is too large.",
    tls: "TLS/certificate error.",
    dns: "The host name could not be resolved.",
  };
  if (map[code]) return map[code];
  const http = /^http_(\d{3})$/.exec(code);
  if (http) return `The upstream answered HTTP ${http[1]}.`;
  return code;
};

/**
 * The first thing an incomplete mapping still needs, phrased as what to pick — for the
 * one-line test status ("Mapping incomplete — pick the wind unit"). Falls back to the raw
 * validation message for anything not recognised.
 */
export const describeMissingField = (errs: string[]): string => {
  const e = errs[0] ?? "";
  if (!e) return "";
  if (e.includes('"wind.unit"')) return "pick the wind unit";
  if (e.includes('"gust.unit"')) return "pick the gust unit";
  if (e.includes('"wind.path"') || e.includes('"wind" is required')) return "pick the wind field";
  if (e.includes('"gust.path"')) return "pick the gust field";
  if (e.includes('"dir.path"')) return "pick the direction field";
  if (e.includes('"time.tz"')) return "pick a time zone";
  if (e.startsWith("Local time needs")) return "pick the date/time field(s)";
  if (e.includes('"time.path"')) return "pick the time field";
  if (e.includes('"time"')) return "pick a time mode";
  return e;
};

/** Strip the query string / fragment (they may carry a secret). */
export const maskUrl = (url: string | null | undefined): string => {
  if (!url) return "";
  const idx = url.search(/[?#]/);
  return idx < 0 ? url : `${url.slice(0, idx)}?…`;
};
